import { useMemo, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Truck, Plus, X as IconX, ArrowRight, Trash2, RotateCcw, Search, CircleDashed, MoreVertical, Info } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { DeliveryVehicleIcon } from '../../components/DeliveryVehicleIcon';
import { DeliveryAgentsPanel } from './DeliveryAgentsPanel';
import { CustomSelect } from '../../components/CustomSelect';
import { SideDrawer } from '../../components/SideDrawer';
import { FilterPopover } from '../../components/FilterPopover';
import {
  ROLES, effectiveBranchesOf, findBranch, catalogProductsOf,
  DELIVERY_STAGES, DELIVERY_STAGE_META,
  deliveriesOf, deliveryAgentsOf, findDeliveryAgent,
  addDelivery, updateDelivery, deleteDelivery, advanceDelivery, nextDeliveryStage,
  refreshDeliveries, getOrgSettings,
} from '../../data/store';
import { useSharedStore } from '../../hooks/useSharedStore';
import { useWebSocket } from '../../hooks/useWebSocket';

const today = () => new Date().toISOString().slice(0, 10);
const money = (n) => `₹${Number(n || 0).toFixed(2)}`;

// ---- Assign agent modal ----
const AssignAgentModal = ({ delivery, agents, accent, onClose, onAssign }) => (
  <SideDrawer onClose={onClose}>
    <header className="px-5 py-4 border-b border-[var(--theme-tertiary)] flex items-center justify-between shrink-0">
      <div>
        <h3 className="text-base font-extrabold text-[#0d0d0d]">Assign delivery agent</h3>
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">{delivery.customer} · {delivery.id}</p>
      </div>
      <button type="button" onClick={onClose} aria-label="Close" className="p-2 rounded-full text-slate-400 hover:text-rose-500 hover:bg-rose-50">
        <IconX size={16} />
      </button>
    </header>
    <div className="px-5 py-4 flex-1 overflow-y-auto custom-scrollbar flex flex-col gap-2">
      {delivery.agentId && (
        <button type="button" onClick={() => onAssign(null)}
          className="w-full flex items-center gap-3 rounded-lg border border-rose-100 px-3 py-2.5 text-left hover:bg-rose-50">
          <span className="w-9 h-9 rounded-lg bg-rose-50 text-rose-500 flex items-center justify-center shrink-0" aria-hidden="true"><IconX size={15} /></span>
          <span className="text-xs font-semibold uppercase tracking-[0.12em] text-rose-500">Unassign current agent</span>
        </button>
      )}
      {agents.map((agent) => {
        const active = delivery.agentId === agent.id;
        const available = agent.status === 'available';
        return (
          <button key={agent.id} type="button" onClick={() => onAssign(agent.id)}
            className={`w-full flex items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors ${active ? 'border-[var(--theme-primary)] bg-[var(--theme-primary-soft)]' : 'border-[var(--theme-tertiary)] hover:border-[var(--theme-primary-tint)]'
              }`}>
            <span className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-bold text-[11px] shrink-0" style={{ background: accent }} aria-hidden="true">
              {agent.name.split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase()}
            </span>
            <span className="flex-1 min-w-0">
              <span className="block text-[13px] font-semibold text-[#0d0d0d] truncate">{agent.name}</span>
              <span className="block text-[11px] font-medium text-slate-400 inline-flex items-center gap-1">
                <DeliveryVehicleIcon vehicle={agent.vehicle} size={10} aria-hidden="true" /> {agent.vehicle} · {agent.mobileNumber}
              </span>
            </span>
            <span className={`text-[11px] font-semibold uppercase tracking-[0.12em] px-2 py-0.5 rounded-lg shrink-0 ${available ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
              {available ? 'Free' : 'Busy'}
            </span>
          </button>
        );
      })}
      {agents.length === 0 && (
        <div className="py-10 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">No agents at this branch.</p>
          <p className="text-[11px] font-medium text-slate-400 mt-1">Add agents from the Agents tab.</p>
        </div>
      )}
    </div>
    <footer className="px-5 py-3 border-t border-[var(--theme-tertiary)] flex justify-end shrink-0" style={{ background: 'var(--theme-secondary)' }}>
      <button type="button" onClick={onClose} className="px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Done</button>
    </footer>
  </SideDrawer>
);

AssignAgentModal.propTypes = {
  delivery: PropTypes.object.isRequired,
  agents: PropTypes.arrayOf(PropTypes.object).isRequired,
  accent: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onAssign: PropTypes.func.isRequired,
};

// ---- New delivery modal ----
const NewDeliveryModal = ({ org, branches, defaultBranchId, lockedBranchId, accent, onClose, onCreate }) => {
  const [customer, setCustomer] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [branchId, setBranchId] = useState(defaultBranchId || branches[0]?.id || '');
  const [method, setMethod] = useState('Card');
  const [note, setNote] = useState('');
  const [fee, setFee] = useState('3.00');
  const [lines, setLines] = useState([{ name: '', qty: '1', price: '' }]);

  const productNames = useMemo(() => catalogProductsOf(org.id).map((p) => p.name), [org.id]);

  const setLine = (i, patch) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const addLine = () => setLines((ls) => [...ls, { name: '', qty: '1', price: '' }]);
  const removeLine = (i) => setLines((ls) => (ls.length > 1 ? ls.filter((_, idx) => idx !== i) : ls));

  const validLines = lines
    .map((l) => ({ name: l.name.trim(), qty: Number(l.qty) || 0, price: Number(l.price) || 0 }))
    .filter((l) => l.name && l.qty > 0);
  const subtotal = validLines.reduce((s, l) => s + l.qty * l.price, 0);
  const total = subtotal + (Number(fee) || 0);
  const canSubmit = customer.trim() && address.trim() && validLines.length > 0;

  const submit = () => {
    if (!canSubmit) return;
    onCreate({
      orgId: org.id,
      locationId: branchId,
      customer: customer.trim(),
      phone: phone.trim(),
      address: address.trim(),
      lines: validLines.map((l) => ({ name: l.name, label: '', qty: l.qty, price: l.price })),
      subtotal: +subtotal.toFixed(2),
      fee: +(Number(fee) || 0).toFixed(2),
      total: +total.toFixed(2),
      method,
      note: note.trim(),
      status: 'new',
      agentId: null,
      createdAt: today(),
    });
  };

  return (
    <SideDrawer onClose={onClose}>
      <header className="px-5 py-4 border-b border-[var(--theme-tertiary)] flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg text-white flex items-center justify-center" style={{ background: accent }} aria-hidden="true">
            <Truck size={18} />
          </div>
          <div>
            <h3 className="text-base font-extrabold text-[#0d0d0d]">New delivery</h3>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Raise a delivery order</p>
          </div>
        </div>
        <button type="button" onClick={onClose} aria-label="Close" className="p-2 rounded-full text-slate-400 hover:text-rose-500 hover:bg-rose-50">
          <IconX size={16} />
        </button>
      </header>

      <div className="px-5 py-5 flex-1 overflow-y-auto custom-scrollbar flex flex-col gap-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block space-y-1">
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Customer name</span>
            <input type="text" value={customer} onChange={(e) => setCustomer(e.target.value)} placeholder="e.g. Emma Hayes"
              className="w-full rounded-lg bg-gray-50 border border-gray-100 focus:bg-white focus:border-[var(--theme-primary)] outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d]" />
          </label>
          <label className="block space-y-1">
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Phone</span>
            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 555 0100"
              className="w-full rounded-lg bg-gray-50 border border-gray-100 focus:bg-white focus:border-[var(--theme-primary)] outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d]" />
          </label>
          <label className="block space-y-1 sm:col-span-2">
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Delivery address</span>
            <input type="text" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Street, city"
              className="w-full rounded-lg bg-gray-50 border border-gray-100 focus:bg-white focus:border-[var(--theme-primary)] outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d]" />
          </label>
          <label className="block space-y-1">
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Ships from branch</span>
            <CustomSelect
              value={branchId}
              onChange={setBranchId}
              disabled={!!lockedBranchId}
              options={branches.map((b) => ({ value: b.id, label: b.name }))}
              className="w-full border-gray-100 bg-gray-50 text-xs font-medium normal-case tracking-normal py-2 px-3 disabled:opacity-60"
            />
          </label>
          <label className="block space-y-1">
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Payment</span>
            <CustomSelect
              value={method}
              onChange={setMethod}
              options={[
                { value: 'Card', label: 'Card' },
                { value: 'Cash', label: 'Cash on delivery' }
              ]}
              className="w-full border-gray-100 bg-gray-50 text-xs font-medium normal-case tracking-normal py-2 px-3"
            />
          </label>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Items</span>
            <button type="button" onClick={addLine} className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.12em] text-[var(--theme-primary)]">
              <Plus size={12} /> Add item
            </button>
          </div>
          <datalist id="delivery-product-names">
            {productNames.map((n) => <option key={n} value={n} />)}
          </datalist>
          <div className="flex flex-col gap-2">
            {lines.map((line, i) => (
              <div key={i} className="flex items-center gap-2">
                <input type="text" list="delivery-product-names" value={line.name} onChange={(e) => setLine(i, { name: e.target.value })} placeholder="Item name"
                  className="flex-1 min-w-0 rounded-lg bg-gray-50 border border-gray-100 focus:bg-white focus:border-[var(--theme-primary)] outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d]" />
                <input type="number" min="1" value={line.qty} onChange={(e) => setLine(i, { qty: e.target.value })} placeholder="Qty" aria-label="Quantity"
                  className="w-16 rounded-lg bg-gray-50 border border-gray-100 focus:bg-white focus:border-[var(--theme-primary)] outline-none px-2 py-2 text-xs font-medium text-[#0d0d0d] text-center" />
                <input type="number" min="0" step="0.01" value={line.price} onChange={(e) => setLine(i, { price: e.target.value })} placeholder="Price" aria-label="Unit price"
                  className="w-20 rounded-lg bg-gray-50 border border-gray-100 focus:bg-white focus:border-[var(--theme-primary)] outline-none px-2 py-2 text-xs font-medium text-[#0d0d0d] text-right" />
                <button type="button" onClick={() => removeLine(i)} disabled={lines.length === 1} aria-label="Remove item"
                  className="p-1.5 rounded-md text-slate-400 hover:text-rose-500 hover:bg-rose-50 disabled:opacity-30">
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block space-y-1">
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Delivery fee (₹)</span>
            <input type="number" min="0" step="0.01" value={fee} onChange={(e) => setFee(e.target.value)}
              className="w-full rounded-lg bg-gray-50 border border-gray-100 focus:bg-white focus:border-[var(--theme-primary)] outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d]" />
          </label>
          <label className="block space-y-1">
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Note (optional)</span>
            <input type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Leave at the door"
              className="w-full rounded-lg bg-gray-50 border border-gray-100 focus:bg-white focus:border-[var(--theme-primary)] outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d]" />
          </label>
        </div>
      </div>

      <footer className="px-5 py-4 border-t border-[var(--theme-tertiary)] flex items-center justify-between gap-3 shrink-0" style={{ background: 'var(--theme-secondary)' }}>
        <div className="text-[11px] font-bold text-slate-500">
          Subtotal {money(subtotal)} · Fee {money(Number(fee) || 0)} ·
          <span className="text-[#0d0d0d]"> Total {money(total)}</span>
        </div>
        <div className="flex items-center gap-3">
          <button type="button" onClick={onClose} className="px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Cancel</button>
          <button type="button" onClick={submit} disabled={!canSubmit}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-white text-xs font-semibold uppercase tracking-[0.14em] disabled:opacity-50"
            style={{ background: accent }}>
            Create delivery
          </button>
        </div>
      </footer>
    </SideDrawer>
  );
};

NewDeliveryModal.propTypes = {
  org: PropTypes.object.isRequired,
  branches: PropTypes.arrayOf(PropTypes.object).isRequired,
  defaultBranchId: PropTypes.string,
  lockedBranchId: PropTypes.string,
  accent: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
};

// Every value on a card carries one of these.
//
// Stripping the labels to save height was a mistake: it left a column of
// anonymous strings, where a number could be a phone or an order id and "CASH"
// could be somebody's name. The reader should never have to infer what they are
// looking at. The label is the smallest type on the card and never wraps, so it
// costs a line and explains everything under it.
const FieldLabel = ({ children }) => (
  <p className="mb-0.5 truncate text-[9px] font-bold uppercase leading-none tracking-wider text-slate-400">
    {children}
  </p>
);

FieldLabel.propTypes = { children: PropTypes.node.isRequired };

// ---- Delivery card ----
const DeliveryCard = ({ delivery, accent, onAdvance, onCancel, onAssign, onReopen, onDelete, showNotification }) => {
  const agent = delivery.agentId ? findDeliveryAgent(delivery.agentId) : null;
  const meta = DELIVERY_STAGE_META[delivery.status];
  const next = nextDeliveryStage(delivery.status);
  const cancelled = delivery.status === 'cancelled';
  const itemCount = (delivery.lines || []).reduce((s, l) => s + l.qty, 0);
  // A delivered or cancelled order is finished, and no longer needs to say
  // whether there is money to collect.
  const settled = delivery.status === 'delivered' || cancelled;

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // A menu opened by tapping has to be closable by tapping somewhere else —
  // there is no "move the pointer away" on a touch screen. `pointerdown` covers
  // mouse, pen and finger with one listener, and it fires before the card's own
  // click handling, so dismissing the menu never doubles as picking something.
  useEffect(() => {
    if (!menuOpen) return undefined;

    const onPointerDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  // Every item closes the menu, and stops the tap reaching the card underneath.
  const runAction = (fn) => (e) => {
    e.stopPropagation();
    setMenuOpen(false);
    fn();
  };

  // Cash is collected at the door; a card order — which is what paying online
  // is, in this system — is already settled. Both say so, because an empty
  // space cannot be read: it looks the same whether the order is paid, the
  // payment method was never set, or something failed to draw. The one that
  // needs doing is dark; the one that needs nothing recedes.
  const payment = String(delivery.method || '').trim();
  const collectCash = payment.toLowerCase() === 'cash';

  return (
    <motion.article layout initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      draggable={!cancelled && !!agent}
      onDragStart={(e) => {
        if (cancelled || !agent) {
          e.preventDefault();
          return;
        }
        e.dataTransfer.setData('deliveryId', delivery.id);
        e.dataTransfer.effectAllowed = 'move';
      }}
      className="bg-white border border-[var(--theme-tertiary)] rounded-md p-2.5 flex flex-col gap-2 cursor-grab active:cursor-grabbing relative hover:z-20 group shadow-[0_2px_12px_rgba(0,0,0,0.03)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.06)] transition-all w-full min-w-0">
      
      {/* Accent left border */}
      <div className="absolute left-0 top-0 bottom-0 w-[4px] rounded-l-md" style={{ background: meta.color }} />

      <header className="flex items-start justify-between gap-2 min-w-0 pl-1">
        <div className="min-w-0 flex-1">
          <FieldLabel>Customer Name</FieldLabel>
          <p className="text-[13px] font-bold text-[#0d0d0d] uppercase truncate leading-tight" title={delivery.customer}>
            {delivery.customer}
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {/* Actions.

              The menu used to open on hover, which a touch screen does not
              have: a tap either did nothing or left it stuck open with nothing
              to dismiss it. It opens on a tap now, and closes on the next tap
              anywhere, on Escape, and on choosing something.

              It is also sized to sit inside the card. At 192px wide, dropped
              below the button, it hung off a card that is now half the height
              it used to be — and the board's horizontal scroll clipped
              whatever escaped. */}
          <div className="relative shrink-0" ref={menuRef}>
            <button type="button"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              aria-label="Delivery actions"
              onClick={(e) => { e.stopPropagation(); setMenuOpen((open) => !open); }}
              className={`flex h-6 w-6 items-center justify-center rounded transition-colors ${
                menuOpen ? 'bg-slate-100 text-slate-700' : 'text-slate-400 hover:bg-slate-100 hover:text-slate-700'
              }`}>
              <MoreVertical size={16} />
            </button>

            {menuOpen && (
              <div role="menu"
                className="absolute right-0 top-full z-30 mt-1 flex w-40 max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-md border border-slate-200 bg-white shadow-[0_4px_20px_rgba(0,0,0,0.12)]">
                {cancelled ? (
                  <>
                    <button type="button" role="menuitem" onClick={runAction(() => onReopen(delivery))}
                      className="flex items-center gap-2 px-2.5 py-1.5 text-left text-[11px] font-medium text-slate-700 transition-colors hover:bg-slate-50">
                      <RotateCcw size={12} className="shrink-0 text-slate-400" />
                      <span className="truncate">Reopen</span>
                    </button>
                    <button type="button" role="menuitem" onClick={runAction(() => onDelete(delivery))}
                      className="flex items-center gap-2 border-t border-slate-100 px-2.5 py-1.5 text-left text-[11px] font-medium text-rose-600 transition-colors hover:bg-rose-50">
                      <Trash2 size={12} className="shrink-0 text-rose-400" />
                      <span className="truncate">Delete</span>
                    </button>
                  </>
                ) : (
                  <>
                    {next && (
                      <button type="button" role="menuitem"
                        title={!agent ? 'Please assign an agent first' : ''}
                        onClick={runAction(() => {
                          if (!agent) {
                            if (showNotification) showNotification('Please assign an agent first', 'error');
                            else alert('Please assign an agent first.');
                            return;
                          }
                          onAdvance(delivery);
                        })}
                        className={`flex items-center gap-2 px-2.5 py-1.5 text-left text-[11px] font-medium transition-colors hover:bg-slate-50 ${
                          !agent ? 'text-slate-400 opacity-60' : 'text-slate-700'
                        }`}>
                        <ArrowRight size={12} className="shrink-0" style={{ color: !agent ? undefined : accent }} />
                        <span className="truncate">Move to {DELIVERY_STAGE_META[next].label}</span>
                      </button>
                    )}
                    <button type="button" role="menuitem" onClick={runAction(() => onAssign(delivery))}
                      className={`flex items-center gap-2 px-2.5 py-1.5 text-left text-[11px] font-medium text-slate-700 transition-colors hover:bg-slate-50 ${next ? 'border-t border-slate-100' : ''}`}>
                      {agent
                        ? <DeliveryVehicleIcon vehicle={agent.vehicle} size={12} className="shrink-0 text-slate-400" />
                        : <CircleDashed size={12} className="shrink-0 text-[var(--theme-primary)]" />}
                      <span className="truncate">{agent ? agent.name : 'Assign Agent'}</span>
                    </button>
                    <button type="button" role="menuitem" onClick={runAction(() => onCancel(delivery))}
                      className="flex items-center gap-2 border-t border-slate-100 px-2.5 py-1.5 text-left text-[11px] font-medium text-rose-600 transition-colors hover:bg-rose-50">
                      <IconX size={12} className="shrink-0 text-rose-500" />
                      <span className="truncate">Cancel order</span>
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Where it goes. The card used to show our own branch here, under the
          label "Location" — the one thing a delivery card must answer, and it
          answered with something the reader already knew. Two lines at most,
          and it breaks mid-word rather than pushing out, because an address is
          the one field with no predictable length. */}
      <div className="pl-1">
        <FieldLabel>Delivery Address</FieldLabel>
        <p className="text-[11px] font-medium leading-snug text-slate-600 line-clamp-2 break-words"
          title={delivery.address || undefined}>
          {delivery.address || '—'}
        </p>
      </div>

      {/* The number is dialled, not read out, so on a phone or a tablet it is a
          link. `draggable={false}` keeps the browser's own link-dragging from
          stealing the card's drag. */}
      <div className="pl-1">
        <FieldLabel>Customer Number</FieldLabel>
        <p className={`truncate text-[12px] font-bold ${delivery.phone ? 'text-[#0d0d0d]' : 'text-slate-400'}`}
          title={delivery.phone || undefined}>
          {delivery.phone || '—'}
        </p>
      </div>

      <div className="min-w-0 pl-1">
        <FieldLabel>Items</FieldLabel>
        <p className="truncate text-[11.5px] font-bold text-[#0d0d0d]">
          {itemCount} item{itemCount === 1 ? '' : 's'}
        </p>
      </div>

      <div className="mt-auto grid grid-cols-2 gap-x-2 border-t border-slate-100 pl-1 pr-1 pt-2">
        <div className="min-w-0">
          <FieldLabel>Amount</FieldLabel>
          <p className="truncate text-[13px] font-bold text-[#0d0d0d]">{money(delivery.total)}</p>
        </div>
        <div className="min-w-0">
          <FieldLabel>Payment Mode</FieldLabel>
          {payment ? (
            <p className={`truncate text-[11.5px] font-extrabold uppercase tracking-wide ${
              collectCash && !settled ? 'text-emerald-700' : 'text-slate-500'
            }`}>
              {collectCash ? 'Cash' : 'Paid'}
            </p>
          ) : (
            <p className="text-[11.5px] font-bold text-slate-400">—</p>
          )}
        </div>
      </div>

      {delivery.note && (
        <div className="flex items-start gap-1.5 rounded-lg border border-amber-100/50 bg-amber-50/50 p-2 text-[10.5px] font-medium text-amber-900/80">
          <Info size={11} className="mt-0.5 shrink-0 text-amber-500" />
          <p className="leading-snug line-clamp-2 break-words">{delivery.note}</p>
        </div>
      )}
    </motion.article>
  );
};

DeliveryCard.propTypes = {
  delivery: PropTypes.object.isRequired,
  accent: PropTypes.string.isRequired,
  onAdvance: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onAssign: PropTypes.func.isRequired,
  onReopen: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

// ---- Delivery tab ----
export const DeliveryTab = ({ org, role, showNotification, lockedBranchId }) => {
  const { version: dataVersion } = useSharedStore();
  const [version, setVersion] = useState(0);
  const bump = () => setVersion((v) => v + 1);
  const [filters, setFilters] = useState({});
  // Orders / Agents is navigation, not a filter. It used to live inside the
  // filter popover as a "View" category, which took four clicks to reach and
  // hid the Agents section from anyone who never opened the filter.
  const [view, setView] = useState('orders');
  const branchFilter = filters.branches?.[0] || 'all';
  const scopeBranch = lockedBranchId || (branchFilter !== 'all' ? branchFilter : null);
  const [search, setSearch] = useState('');
  const [assignTarget, setAssignTarget] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [dragOverStage, setDragOverStage] = useState(null);
  const agentsPanelRef = useRef();


  const { isConnected, lastMessage } = useWebSocket(org.id);

  useEffect(() => {
    if (lastMessage?.type === 'DELIVERY_UPDATED') {
      refreshDeliveries(org.id);
      bump(); // Force re-render just in case
    }
  }, [lastMessage, org.id]);

  const accent = org.theme.primary;
  const isOrgAdmin = role === ROLES.ORG_ADMIN;
  const isBranchAdmin = role === ROLES.BRANCH_ADMIN;
  const branches = useMemo(() => effectiveBranchesOf(org.id), [org.id, dataVersion]);
  const branchesEnabled = useMemo(() => getOrgSettings(org.id).branchesEnabled, [org.id, dataVersion]);

  const list = useMemo(() => {
    let rows = deliveriesOf(org.id, scopeBranch);
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter((d) => `${d.customer} ${d.address} ${d.id} ${d.phone}`.toLowerCase().includes(q));
    }
    return rows;
  }, [org.id, scopeBranch, search, version, dataVersion]);

  const byStage = useMemo(() => {
    const map = {};
    DELIVERY_STAGES.forEach((s) => { map[s] = []; });
    map.cancelled = [];
    list.forEach((d) => { (map[d.status] || map.new).push(d); });
    return map;
  }, [list, dataVersion]);

  const handleDrop = (e, targetStage) => {
    e.preventDefault();
    setDragOverStage(null);
    const id = e.dataTransfer.getData('deliveryId');
    if (!id) return;
    
    const d = list.find((x) => x.id === id);
    if (d && d.status !== targetStage) {
      updateDelivery(id, { status: targetStage });
      if (showNotification) showNotification(`Moved ${d.customer} to ${DELIVERY_STAGE_META[targetStage].label}`, 'success');
      bump();
    }
  };

  const advance = (d) => {
    advanceDelivery(d.id);
    const next = nextDeliveryStage(d.status);
    if (showNotification && next) showNotification(`${d.customer} → ${DELIVERY_STAGE_META[next].label}`, 'success');
    bump();
  };
  const cancel = (d) => {
    updateDelivery(d.id, { status: 'cancelled' });
    if (showNotification) showNotification(`Delivery for ${d.customer} cancelled.`);
    bump();
  };
  const reopen = (d) => {
    updateDelivery(d.id, { status: 'new' });
    if (showNotification) showNotification(`Delivery for ${d.customer} reopened.`, 'success');
    bump();
  };
  const removeRow = (d) => {
    deleteDelivery(d.id);
    if (showNotification) showNotification('Delivery removed.', 'success');
    bump();
  };
  const assign = (agentId) => {
    if (!assignTarget) return;
    updateDelivery(assignTarget.id, { agentId });
    if (showNotification) {
      const agent = agentId ? findDeliveryAgent(agentId) : null;
      showNotification(agent ? `${agent.name} assigned to ${assignTarget.customer}.` : 'Agent unassigned.', 'success');
    }
    setAssignTarget(null);
    bump();
  };
  const createDelivery = (record) => {
    addDelivery(record);
    if (showNotification) showNotification(`Delivery for ${record.customer} created.`, 'success');
    setNewOpen(false);
    bump();
  };

  const newDefaultBranch = lockedBranchId || (branchFilter !== 'all' ? branchFilter : branches[0]?.id);

  const DELIVERY_TABS = [
    { id: 'orders', label: 'Orders' },
    { id: 'agents', label: 'Agents' },
  ];

  return (
    <div className="flex flex-col gap-4">
      {/* Orders / Agents. Same tab bar as OrgAdminProfile so the two pages
          behave alike. Switching clears the search box, which otherwise carries
          a delivery search over into the agent list and shows nothing. */}
      <div className="flex items-center gap-6 border-b border-gray-100">
        {DELIVERY_TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => { setView(t.id); setSearch(''); }}
            className={`relative px-4 py-3 text-xs font-bold uppercase tracking-[0.1em] transition-colors ${
              view === t.id ? 'text-[var(--theme-primary)]' : 'text-slate-400 hover:text-slate-600'
            }`}
          >
            {t.label}
            {view === t.id && (
              // Shared layoutId, so the underline slides across to the tab you
              // click instead of disappearing from one and appearing on the other.
              <motion.span
                layoutId="deliveryTabUnderline"
                className="absolute left-0 right-0 -bottom-[1px] h-[2px] rounded-full"
                style={{ background: 'var(--theme-primary)' }}
                transition={{ type: 'spring', stiffness: 420, damping: 34 }}
              />
            )}
          </button>
        ))}
      </div>

      {/* Toolbar */}
      <section className="bg-white border border-[var(--theme-tertiary)] rounded-lg px-5 py-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center flex-1">
          <div className="relative flex-1 max-w-sm">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
            <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={view === 'orders' ? "Search deliveries…" : "Search agents…"}
              className="w-full bg-[var(--theme-secondary)] border border-[var(--theme-tertiary)] focus:bg-white focus:border-[var(--theme-primary)] rounded-lg pl-9 pr-4 py-2 text-xs font-medium text-[#0d0d0d] outline-none" />
          </div>

          <div className="flex items-center shrink-0">
            <FilterPopover 
              accent={accent}
              initialFilters={filters}
              onApply={setFilters}
              align="left"
              categories={[
                ...((!lockedBranchId && branches.length > 1 && branchesEnabled) ? [{
                  id: 'branches',
                  label: 'Branch',
                  options: branches.map(b => ({ id: b.id, label: b.name }))
                }] : [])
              ]}
            />
          </div>
          {(lockedBranchId && branchesEnabled) && (
            <span className="inline-flex items-center gap-2 px-3 py-2 rounded-md border bg-white text-[11px] font-bold text-[#0d0d0d]" style={{ borderColor: 'var(--theme-tertiary)' }}>
              {findBranch(lockedBranchId)?.name || 'Branch'}
            </span>
          )}
        </div>

        <div className="flex items-center gap-3 self-start lg:self-auto shrink-0">
          {!isConnected && (
            <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-red-50 text-red-600 border border-red-100 shrink-0">
              <span className="w-2 h-2 rounded-full bg-red-500" aria-hidden="true" />
              <span className="text-[10px] font-extrabold uppercase tracking-wider">Offline</span>
            </div>
          )}

          {view === 'agents' && (isOrgAdmin || isBranchAdmin) && (
            <button type="button" onClick={() => agentsPanelRef.current?.openCreate()}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-xs font-semibold uppercase tracking-[0.14em] shadow-lg shrink-0"
              style={{ background: accent, boxShadow: `0 12px 24px -12px ${accent}` }}>
              <Plus size={14} aria-hidden="true" /> New agent
            </button>
          )}
        </div>
      </section>

      <AnimatePresence mode="wait" initial={false}>
      {view === 'agents' ? (
        <motion.div
          key="agents"
          className="flex flex-col gap-4"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.18, ease: 'easeOut' }}
        >
        <DeliveryAgentsPanel ref={agentsPanelRef} org={org} branchesEnabled={branchesEnabled} search={search} showNotification={showNotification} canEdit={isOrgAdmin || isBranchAdmin} lockedBranchId={lockedBranchId} />
        </motion.div>
      ) : (
        <motion.div
          key="orders"
          className="flex flex-col gap-4"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.18, ease: 'easeOut' }}
        >

          {/* Pipeline board */}
          <section className="flex flex-col md:flex-row gap-4 overflow-x-auto pb-4 w-full min-w-0 custom-scrollbar">
            {DELIVERY_STAGES.map((stage) => {
              const meta = DELIVERY_STAGE_META[stage];
              const cards = byStage[stage];
              return (
                <div 
                  key={stage} 
                  className="flex flex-col gap-3 shrink-0 w-72 lg:flex-1 lg:w-auto"
                  style={{ minWidth: '210px' }}
                  onDragOver={(e) => { e.preventDefault(); setDragOverStage(stage); }}
                  onDragLeave={(e) => { e.preventDefault(); if (dragOverStage === stage) setDragOverStage(null); }}
                  onDrop={(e) => handleDrop(e, stage)}
                >
                  <header className="flex items-center justify-between px-1">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: meta.color }} aria-hidden="true" />
                      <h3 className="text-xs font-semibold uppercase tracking-[0.14em] text-[#0d0d0d]">{meta.label}</h3>
                    </div>
                    <span className="text-[11px] font-semibold tabular-nums" style={{ color: meta.color }}>
                      {cards.length}
                    </span>
                  </header>
                  <div className={`flex flex-col gap-3 min-h-[80px] rounded-lg border border-dashed p-2.5 flex-1 transition-colors ${dragOverStage === stage ? 'border-[var(--theme-primary)] bg-[var(--theme-primary-soft)]' : 'border-[var(--theme-tertiary)]'}`}>
                    <AnimatePresence>
                      {cards.map((d) => (
                        <DeliveryCard
                          key={d.id}
                          delivery={d}
                          accent={accent}
                          onAdvance={advance}
                          onCancel={cancel}
                          onAssign={setAssignTarget}
                          onReopen={reopen}
                          onDelete={removeRow}
                          showNotification={showNotification}
                        />
                      ))}
                    </AnimatePresence>
                    {cards.length === 0 && (
                      <p className="py-6 text-center text-xs font-semibold uppercase tracking-[0.14em] text-slate-300">{meta.hint}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </section>

          {/* Cancelled */}
          {byStage.cancelled.length > 0 && (
            <section className="flex flex-col gap-3">
              <header className="flex items-center gap-2 px-1">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: DELIVERY_STAGE_META.cancelled.color }} aria-hidden="true" />
                <h3 className="text-xs font-semibold uppercase tracking-[0.14em] text-[#0d0d0d]">Cancelled</h3>
                <span className="text-[11px] font-semibold tabular-nums" style={{ color: DELIVERY_STAGE_META.cancelled.color }}>
                  {byStage.cancelled.length}
                </span>
              </header>
              <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-4 items-start">
                <AnimatePresence>
                  {byStage.cancelled.map((d) => (
                    <DeliveryCard
                      key={d.id}
                      delivery={d}
                      accent={accent}
                      onAdvance={advance}
                      onCancel={cancel}
                      onAssign={setAssignTarget}
                      onReopen={reopen}
                      onDelete={removeRow}
                      showNotification={showNotification}
                    />
                  ))}
                </AnimatePresence>
              </div>
            </section>
          )}


        </motion.div>
      )}
      </AnimatePresence>

      {assignTarget && (
        <AssignAgentModal
          delivery={assignTarget}
          agents={deliveryAgentsOf(org.id, assignTarget.locationId)}
          accent={accent}
          onClose={() => setAssignTarget(null)}
          onAssign={assign}
        />
      )}
      {newOpen && (
        <NewDeliveryModal
          org={org}
          branches={branches}
          defaultBranchId={newDefaultBranch}
          lockedBranchId={lockedBranchId}
          accent={accent}
          onClose={() => setNewOpen(false)}
          onCreate={createDelivery}
        />
      )}
    </div>
  );
};

DeliveryTab.propTypes = {
  org: PropTypes.object.isRequired,
  role: PropTypes.string,
  showNotification: PropTypes.func,
  lockedBranchId: PropTypes.string,
};
